import { useEffect, useRef, useState } from 'react'
import DocumentLink from '../documents/DocumentLink'
import { ErrorState } from '../state'
import { formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import type { ClientView } from './context'
import { useRead } from './useRead'
interface Receipts {
  items: {
    receipt_id: string
    document_id: string | null
    document_present: boolean
    document_title: string | null
    result: string
    received_at: string
  }[]
  total: number
  page: number
  page_size: number
}
/** Source context, not an invented Drive URL or an attribution mutation. */
export default function FolderDocuments({
  client,
  folder,
  onClose,
}: {
  client: ClientView
  folder?: ClientView['fund_folders'][number]
  onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [page, setPage] = useState(1)
  const [retry, setRetry] = useState(0)
  const params = new URLSearchParams({ client_id: client.id, page: String(page), page_size: '30' })
  if (folder) params.set('fund_folder_id', folder.id)
  const read = useRead<Receipts>('/api/intake/receipts?' + params, retry)
  useEffect(() => {
    const active = document.activeElement
    const element = dialog.current
    element?.showModal()
    return () => {
      element?.close()
      if (active instanceof HTMLElement && active.isConnected) active.focus()
    }
  }, [])
  return (
    <dialog
      ref={dialog}
      className="nav-folder-dialog"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      aria-labelledby="folder-context-title"
    >
      <header>
        <h2 id="folder-context-title">{client.display_name ?? 'Client name not recorded'}</h2>
        <button onClick={onClose}>Close ×</button>
      </header>
      <p>Source: {client.source.name}</p>
      <p className="nav-folder-reference">{folder?.folder_reference ?? client.folder_reference}</p>
      <h3>Documents received</h3>
      {read.loading ? <p role="status">Loading documents…</p> : null}
      {read.error ? (
        <ErrorState
          what="source documents"
          error={read.error}
          onRetry={() => setRetry((n) => n + 1)}
        />
      ) : null}
      {read.value?.items.map((item) => (
        <div className="nav-receipt" key={item.receipt_id}>
          {item.document_id && item.document_present ? (
            <DocumentLink
              document={{ document_id: item.document_id, display_title: item.document_title }}
            />
          ) : (
            <span>{item.document_title ?? 'Document receipt'}</span>
          )}
          <small>
            {formatOfficerDate(item.received_at)} · {humanizeToken(item.result)}
          </small>
        </div>
      ))}
      {read.value?.total === 0 ? <p>No document receipts in this folder.</p> : null}
      {read.value && (page > 1 || page * read.value.page_size < read.value.total) ? (
        <nav className="nav-paging" aria-label="Document receipt pages">
          <button disabled={page === 1} onClick={() => setPage((n) => n - 1)}>
            Previous
          </button>
          <span>Page {page}</span>
          <button
            disabled={page * read.value.page_size >= read.value.total}
            onClick={() => setPage((n) => n + 1)}
          >
            Next
          </button>
        </nav>
      ) : null}
    </dialog>
  )
}
